/**
 * Mitad izquierda de la ventana de documento (DESIGN.md §11.7, decisión del dueño 03-11), para documentos del SII: dos
 * subpestañas y el botón de plegar.
 *  - «Resumen»: la frase · vigencia en una línea · «Normas de la cabecera» (una fila por par norma-artículo, con conteo, abre
 *    la Biblioteca filtrada) · «Abrir el original en el SII».
 *  - «Evolución»: una línea de estado y una tabla Documento · Fecha · Criterio con este documento y los posteriores que se
 *    refieren a él, en orden cronológico; «Cambia» en rojo. Al pasar el cursor o enfocar una fila, la tarjeta con la frase
 *    exacta, la nota y «Abrir ›»; clic abre ese documento en el párrafo de la cita.
 * «Relacionados» (por tema) no se muestra hasta tener un método medido. También el riel de 56 px del estado plegado y el divisor.
 */
import { KeyboardEvent, MouseEvent, PointerEvent, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, FileText, History, Link as LinkIcon, PanelLeftClose, PanelLeftOpen, Scale } from 'lucide-react';
import { Barra, FOCO } from '../biblioteca/piezas';
import type { CargaCadena } from '../../hooks/useDocumento';
import { ANCHO_DEFECTO, ANCHO_MAX, ANCHO_MIN, acotar } from '../../hooks/usePanelDocumento';
import { Cadena, Criterio, DocCitado, enlaceNorma, etiquetaCorta, evolucion, rotuloModificacion } from '../../utils/cadena';
import { fechaCorta } from '../../utils/fechas';
import { miles } from '../../utils/bibliotecas';

export type AbrirOtro = (id: string, parrafo?: number | null) => void;

const FILA = `h-8 grid grid-cols-[18px_minmax(0,1fr)_auto] items-center gap-x-2 -mx-2 px-2 rounded-[7px] text-[13.5px] text-slate-800 ${FOCO}`;
const ROTULO = 'text-[12px] font-medium text-slate-500';
/** «Oficio 2723» → «2723». */
const numeroDe = (etiqueta: string) => (/(\d[\d.]*)\s*$/.exec(etiqueta) || ['', ''])[1] || '';

/** Clic normal abre dentro de la ventana; Ctrl, Shift o botón central dejan al navegador abrir otra pestaña. */
const clicEnVentana = (abrir: AbrirOtro | undefined, id: string, parrafo: number | null) => (e: MouseEvent<HTMLAnchorElement>) => {
  if (!abrir || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
  e.preventDefault();
  abrir(id, parrafo);
};

const rutaCita = (d: DocCitado) => `/documento/${d.id}${d.parrafo ? `#p-${d.parrafo}` : ''}`;

/**
 * La frase se muestra como cita solo si es un pasaje: cuando la nota va intercalada en el PDF el backend manda la nota
 * misma («Ver Oficio N° 2723 de 2022.») y la tarjeta dice solo dónde lo cita (lead, 01-11). `numero` es el de este documento.
 */
export function fraseEsPasaje(frase: string | null | undefined, numero: string): boolean {
  const f = (frase || '').trim();
  if (!f) return false;
  if (/^ver\s/i.test(f)) return false;
  const n = numero.replace(/^0+(?=\d)/, '');
  if (f.length < 60 && n && f.includes(n)) return false;
  return true;
}

/** Tarjeta de una cita (340 px): la frase exacta, dónde está y «Abrir ›». Fija en pantalla, a la derecha del panel. */
function Tarjeta({ id, cita, numero, origen, abrir, onEntrar, onSalir }: { id: string; cita: DocCitado; numero: string; origen: DOMRect; abrir?: AbrirOtro; onEntrar: () => void; onSalir: (e?: { relatedTarget: EventTarget | null }) => void }) {
  const alto = typeof window !== 'undefined' ? window.innerHeight : 800;
  const top = Math.max(8, Math.min(origen.top - 8, alto - 200));
  return (
    <div
      id={id}
      role="group"
      aria-label={`Cita en el ${etiquetaCorta(cita)}`}
      onMouseEnter={onEntrar}
      onMouseLeave={onSalir}
      style={{ position: 'fixed', left: origen.right + 10, top, width: 340, zIndex: 20 }}
      className="bg-white rounded-[10px] px-3.5 py-3 shadow-[0_0_0_1px_rgba(15,23,42,.08),0_12px_32px_rgba(15,23,42,.14)]"
    >
      {fraseEsPasaje(cita.frase, numero) && <p className="text-[13px] leading-[1.55] text-slate-800">«{cita.frase}»</p>}
      <div className={`flex items-center gap-2.5 text-[12px] text-slate-500 ${fraseEsPasaje(cita.frase, numero) ? 'mt-2' : ''}`}>
        {cita.en_nota && cita.nota != null ? <span>lo cita en la <b className="font-medium text-slate-700">nota {cita.nota}</b></span> : <span>lo cita</span>}
        <Link to={rutaCita(cita)} onClick={clicEnVentana(abrir, cita.id, cita.parrafo)} onFocus={onEntrar} onBlur={onSalir} className={`ml-auto font-medium text-indigo-700 hover:underline rounded ${FOCO}`}>
          Abrir ›
        </Link>
      </div>
    </div>
  );
}

const COLUMNAS = 'grid grid-cols-[minmax(0,1fr)_96px_112px] gap-x-3 items-center';

/** Una línea de la tabla de Evolución; al pasar el cursor o enfocar, la tarjeta. */
function FilaEvolucion({ cita, criterio, numero, abrir }: { cita: DocCitado; criterio: Criterio; numero: string; abrir?: AbrirOtro }) {
  const [abierta, setAbierta] = useState<DOMRect | null>(null);
  const fila = useRef<HTMLAnchorElement>(null);
  const cierre = useRef<number>();
  const idTarjeta = useId();
  const mostrar = () => { window.clearTimeout(cierre.current); if (fila.current) setAbierta(fila.current.getBoundingClientRect()); };
  const ocultar = () => { window.clearTimeout(cierre.current); cierre.current = window.setTimeout(() => setAbierta(null), 120); };
  useEffect(() => () => window.clearTimeout(cierre.current), []);
  const blur = (e?: { relatedTarget: EventTarget | null }) => {
    const a = e?.relatedTarget;
    if (a instanceof Node && document.getElementById(idTarjeta)?.contains(a)) return;
    ocultar();
  };
  const tecla = (e: KeyboardEvent<HTMLAnchorElement>) => { if (e.key === 'Escape' && abierta) { e.stopPropagation(); setAbierta(null); } };
  const cuando = fechaCorta(cita.fecha_publicacion) || String(cita.year || '');
  return (
    <div className="relative">
      <Link
        ref={fila}
        to={rutaCita(cita)}
        onClick={clicEnVentana(abrir, cita.id, cita.parrafo)}
        onMouseEnter={mostrar}
        onMouseLeave={ocultar}
        onFocus={mostrar}
        onBlur={blur}
        onKeyDown={tecla}
        aria-label={`${etiquetaCorta(cita)}, ${cuando}: ${criterio}`}
        aria-describedby={abierta ? idTarjeta : undefined}
        className={`${COLUMNAS} h-10 px-2.5 -mx-2.5 rounded-lg border-b border-slate-100 text-[14px] text-slate-900 hover:bg-slate-50 ${FOCO} ${abierta ? 'bg-slate-50' : ''}`}
      >
        <span className="truncate">{etiquetaCorta(cita)}</span>
        <span className="text-slate-600 tabular-nums whitespace-nowrap">{cuando}</span>
        <span className={`font-medium ${criterio === 'Cambia' ? 'text-red-700' : 'text-slate-700'}`}>{criterio}</span>
      </Link>
      {abierta && <Tarjeta id={idTarjeta} cita={cita} numero={numero} origen={abierta} abrir={abrir} onEntrar={mostrar} onSalir={blur} />}
    </div>
  );
}

/** Subpestaña «Evolución»: estado en una línea y la tabla Documento · Fecha · Criterio (este documento y los posteriores). */
function Evolucion({ etiqueta, fecha, data, abrir }: { etiqueta: string; fecha: string; data: Cadena; abrir?: AbrirOtro }) {
  const { filas, cambio } = evolucion(data);
  if (filas.length === 0) return <p className="text-[13.5px] text-slate-500 leading-[1.5]">Ningún documento posterior se refiere a este criterio, según AlertAI.</p>;
  return (
    <section aria-label="Evolución del criterio">
      <p className={`text-[14px] font-medium mb-4 ${cambio ? 'text-red-700' : 'text-slate-900'}`}>{cambio ? `El criterio cambió en ${cambio}` : 'El criterio se mantiene'}</p>
      <div className={`${COLUMNAS} pb-1.5 border-b border-gray-200 text-[12px] text-slate-500`} aria-hidden="true">
        <span>Documento</span><span>Fecha</span><span>Criterio</span>
      </div>
      <div className={`${COLUMNAS} h-10 px-2.5 -mx-2.5 rounded-lg bg-slate-50 text-[14px] text-slate-900`}>
        <span className="truncate">{etiqueta}</span>
        <span className="text-slate-600 tabular-nums whitespace-nowrap">{fecha}</span>
        <span className="text-slate-500">Este documento</span>
      </div>
      {filas.map(f => <FilaEvolucion key={f.doc.id} cita={f.doc} criterio={f.criterio} numero={numeroDe(etiqueta)} abrir={abrir} />)}
    </section>
  );
}

type Pestana = 'resumen' | 'evolucion';

export interface PanelResumenProps {
  /** «Oficio 2723». */
  etiqueta: string;
  fecha: string;
  frase: string;
  cadena: CargaCadena;
  reintentar: () => void;
  /** Enlace al original cuando la cadena no lo trae. */
  origenRespaldo: string | null;
  abrir?: AbrirOtro;
  onPlegar: () => void;
}

export default function PanelResumen({ etiqueta, fecha, frase, cadena, reintentar, origenRespaldo, abrir, onPlegar }: PanelResumenProps) {
  const [pestana, setPestana] = useState<Pestana>('resumen');
  const d = cadena.data;
  const original = d?.original_url || origenRespaldo;
  const mod = d?.vigencia.modificado_por || null;
  const cambio = d ? evolucion(d).cambio : null;
  const idPanel = useId();
  const pestanas: [Pestana, string][] = [['resumen', 'Resumen'], ['evolucion', 'Evolución']];
  // Pestañas con ← →, como pide el patrón de tabs de WAI-ARIA.
  const flechas = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const otra: Pestana = pestana === 'resumen' ? 'evolucion' : 'resumen';
    setPestana(otra);
    document.getElementById(`${idPanel}-${otra}`)?.focus();
  };
  const estado = cadena.estado === 'cargando' ? (
    <div className="mt-3 space-y-3" role="status" aria-busy="true" aria-label="Cargando la cadena del criterio">
      <Barra ancho="70%" />
      <div className="pt-3 space-y-3"><Barra ancho="55%" /><Barra ancho="60%" /><Barra ancho="50%" /></div>
    </div>
  ) : cadena.estado === 'error' ? (
    <p className="mt-3 text-[13px] text-slate-500" role="alert">
      No se pudo cargar la cadena del criterio.{' '}
      <button type="button" onClick={reintentar} className={`font-medium text-indigo-700 hover:underline rounded ${FOCO}`}>Reintentar</button>
    </p>
  ) : null;
  return (
    <div className="h-full min-h-0 flex flex-col">
      <div className="flex items-end h-11 pl-8 pr-4 border-b border-gray-200 shrink-0">
        <div role="tablist" aria-label="Panel del documento" className="flex items-end gap-5">
          {pestanas.map(([clave, nombre]) => (
            <button
              key={clave}
              id={`${idPanel}-${clave}`}
              type="button"
              role="tab"
              aria-selected={pestana === clave}
              aria-controls={`${idPanel}-panel`}
              tabIndex={pestana === clave ? 0 : -1}
              onClick={() => setPestana(clave)}
              onKeyDown={flechas}
              className={`flex items-center gap-1.5 pb-[11px] -mb-px border-b-2 text-[13.5px] font-medium rounded-t ${FOCO} ${pestana === clave ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
            >
              {nombre}
              {clave === 'evolucion' && cambio && <span className="w-[7px] h-[7px] rounded-full bg-red-700" title="El criterio cambió" aria-hidden="true" />}
            </button>
          ))}
        </div>
        <button type="button" onClick={onPlegar} aria-label="Plegar el panel ([)" title="Plegar el panel ([)" className={`ml-auto mb-2 w-7 h-7 grid place-items-center rounded-[7px] text-slate-500 hover:bg-slate-100 hover:text-slate-900 ${FOCO}`}>
          <PanelLeftClose className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
      <div id={`${idPanel}-panel`} role="tabpanel" aria-labelledby={`${idPanel}-${pestana}`} className="flex-1 min-h-0 overflow-y-auto pl-8 pr-6 pt-[18px] pb-6">
        {pestana === 'resumen' ? (
          <>
            {frase && <p className="text-[17px] font-medium leading-[1.55] tracking-[-.005em] text-slate-900">{frase}</p>}
            {estado || (d && (
              <>
                {/* Vigencia en una línea: gris si nadie lo modifica; roja con enlace cuando la base lo registra */}
                {mod ? (
                  <p className="mt-3 text-[13px] font-medium text-red-700">
                    <Link to={`/documento/${mod.id}`} onClick={clicEnVentana(abrir, mod.id, null)} className={`inline-flex items-center gap-0.5 hover:underline rounded ${FOCO}`}>
                      {rotuloModificacion(mod)} <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
                    </Link>
                  </p>
                ) : (
                  <p className="mt-3 text-[13px] text-slate-500">Sin modificaciones registradas en AlertAI</p>
                )}

                {d.normas.length > 0 && (
                  <section aria-label="Normas de la cabecera" className="mt-4">
                    <h2 className={`${ROTULO} mb-1`}>Normas de la cabecera</h2>
                    {d.normas.map(n => (
                      // Una ley fuera del catálogo de la Biblioteca llega con total null: va sin conteo (03-11: `miles(null)` dejaba la app en blanco).
                      <Link key={`${n.ley}-${n.articulo}`} to={enlaceNorma(n)} className={`${FILA} hover:bg-slate-50`} title={typeof n.total === 'number' ? `${miles(n.total)} ${n.total === 1 ? 'oficio' : 'oficios'} sobre ${n.etiqueta} en la Biblioteca` : undefined}>
                        <Scale className="w-4 h-4 text-slate-500" aria-hidden="true" />
                        <span className="truncate">{n.etiqueta}</span>
                        <span className="flex items-center gap-0.5 text-[12.5px] text-slate-500 tabular-nums">{typeof n.total === 'number' ? miles(n.total) : null}<ChevronRight className="w-3.5 h-3.5" aria-hidden="true" /></span>
                      </Link>
                    ))}
                  </section>
                )}
              </>
            ))}
            {original && cadena.estado !== 'cargando' && (
              <a href={original} target="_blank" rel="noopener noreferrer" className={`${FILA} mt-0.5 hover:bg-slate-50 font-medium text-indigo-700`}>
                <LinkIcon className="w-4 h-4 text-indigo-500" aria-hidden="true" />
                <span className="truncate">Abrir el original en el SII</span>
                <span />
              </a>
            )}
          </>
        ) : (
          estado || (d && <Evolucion etiqueta={etiqueta} fecha={fecha} data={d} abrir={abrir} />)
        )}
      </div>
    </div>
  );
}

/** Riel de 56 px del estado plegado: íconos con insignia y globo; cualquiera despliega el panel. */
export function RielPanel({ cadena, origenRespaldo, onDesplegar }: { cadena: CargaCadena; origenRespaldo: string | null; onDesplegar: () => void }) {
  const d = cadena.data;
  const mod = d?.vigencia.modificado_por || null;
  const original = d?.original_url || origenRespaldo;
  const boton = `group relative w-9 h-9 grid place-items-center rounded-[9px] text-slate-500 hover:bg-slate-100 hover:text-slate-900 ${FOCO}`;
  const globo = (texto: string) => (
    <span role="tooltip" className="pointer-events-none absolute left-[52px] top-1 hidden group-hover:block group-focus-visible:block bg-slate-900 text-white text-[12px] px-2 py-1 rounded-[7px] whitespace-nowrap shadow-[0_6px_18px_rgba(15,23,42,.18)] z-20">{texto}</span>
  );
  const insignia = (n: number) => (
    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-slate-100 text-slate-600 text-[10.5px] font-semibold grid place-items-center ring-1 ring-white tabular-nums" aria-hidden="true">{n}</span>
  );
  const vigencia = mod ? rotuloModificacion(mod) : 'Sin modificaciones registradas en AlertAI';
  const evo = d ? evolucion(d) : { filas: [], cambio: null };
  return (
    <div className="h-full flex flex-col items-center gap-1.5 pt-3.5" aria-label="Resumen plegado">
      <button type="button" onClick={onDesplegar} aria-label="Desplegar el panel ([)" className={`${boton} bg-slate-100 text-slate-900`}>
        <PanelLeftOpen className="w-[18px] h-[18px]" aria-hidden="true" />
        {globo('Desplegar el panel ([)')}
      </button>
      {d && (
        <>
          <button type="button" onClick={onDesplegar} aria-label={`Vigencia: ${vigencia}`} className={`${boton} ${mod ? 'text-red-700' : ''}`}>
            <History className="w-[18px] h-[18px]" aria-hidden="true" />
            {globo(vigencia)}
          </button>
          <button type="button" onClick={onDesplegar} aria-label={`Normas de la cabecera: ${d.normas.length}`} className={boton}>
            <Scale className="w-[18px] h-[18px]" aria-hidden="true" />
            {d.normas.length > 0 && insignia(d.normas.length)}
            {globo('Normas de la cabecera')}
          </button>
          {original && (
            <a href={original} target="_blank" rel="noopener noreferrer" aria-label="Abrir el original en el SII" className={boton}>
              <LinkIcon className="w-[18px] h-[18px]" aria-hidden="true" />
              {globo('Abrir el original en el SII')}
            </a>
          )}
          <button type="button" onClick={onDesplegar} aria-label={`Evolución: ${evo.filas.length}`} className={`${boton} ${evo.cambio ? 'text-red-700' : ''}`}>
            <FileText className="w-[18px] h-[18px]" aria-hidden="true" />
            {evo.filas.length > 0 && insignia(evo.filas.length)}
            {globo(evo.cambio ? `Evolución: el criterio cambió en ${evo.cambio}` : evo.filas.length ? `Evolución: ${evo.filas.length} ${evo.filas.length === 1 ? 'documento posterior' : 'documentos posteriores'}` : 'Ningún documento posterior se refiere a este criterio')}
          </button>
        </>
      )}
    </div>
  );
}

/** Divisor arrastrable entre 320 y 640 px; doble clic vuelve a 470; con teclado, ← → de 16 en 16, Inicio y Fin. */
export function Divisor({ ancho, onAncho }: { ancho: number; onAncho: (n: number) => void }) {
  const inicio = useRef<{ x: number; ancho: number } | null>(null);
  const bajar = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    inicio.current = { x: e.clientX, ancho };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const mover = (e: PointerEvent<HTMLDivElement>) => {
    if (!inicio.current) return;
    onAncho(acotar(inicio.current.ancho + e.clientX - inicio.current.x));
  };
  const soltar = (e: PointerEvent<HTMLDivElement>) => {
    if (!inicio.current) return;
    inicio.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const tecla = (e: KeyboardEvent<HTMLDivElement>) => {
    const paso = { ArrowLeft: -16, ArrowRight: 16 }[e.key as 'ArrowLeft' | 'ArrowRight'];
    if (paso) { e.preventDefault(); onAncho(acotar(ancho + paso)); return; }
    if (e.key === 'Home') { e.preventDefault(); onAncho(ANCHO_MIN); }
    if (e.key === 'End') { e.preventDefault(); onAncho(ANCHO_MAX); }
  };
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Ancho del panel"
      aria-valuemin={ANCHO_MIN}
      aria-valuemax={ANCHO_MAX}
      aria-valuenow={ancho}
      tabIndex={0}
      onPointerDown={bajar}
      onPointerMove={mover}
      onPointerUp={soltar}
      onPointerCancel={soltar}
      onDoubleClick={() => onAncho(ANCHO_DEFECTO)}
      onKeyDown={tecla}
      title="Arrastra para cambiar el ancho; doble clic lo restablece"
      className={`group absolute top-0 bottom-0 -right-[4px] w-[9px] cursor-col-resize touch-none z-10 ${FOCO}`}
    >
      <span aria-hidden="true" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-9 rounded bg-slate-300 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity motion-reduce:transition-none" />
    </div>
  );
}
